import { useState } from 'react';
import { DOCUMENT_CATEGORIES, getCategory } from '@beckersign/shared';

interface Props {
  value: string;
  onChange: (id: string) => void;
  acknowledged: string[];
  onAcknowledge: (ids: string[]) => void;
}

/**
 * The eligibility gate (C-13 … C-16).
 *
 * Blocked categories are not selectable-then-rejected — the block is shown
 * immediately, with the statute, and the create button stays disabled. There is
 * deliberately no override in the UI; overriding requires editing config.
 */
export default function CategoryPicker({ value, onChange, acknowledged, onAcknowledge }: Props) {
  const [touched, setTouched] = useState(false);
  const cat = getCategory(value);

  const allowed = DOCUMENT_CATEGORIES.filter((c) => c.eligibility === 'allowed');
  const warned = DOCUMENT_CATEGORIES.filter((c) => c.eligibility === 'warn');
  const blocked = DOCUMENT_CATEGORIES.filter((c) => c.eligibility === 'block');

  const ackd = cat ? acknowledged.includes(cat.id) : false;

  return (
    <div>
      <label htmlFor="cat">
        <span className="muted">What kind of document is this?</span>
      </label>
      <select
        id="cat" value={value} required
        onChange={(e) => { setTouched(true); onChange(e.target.value); }}
        style={{ width: '100%', padding: '.55rem', minHeight: 44, marginTop: '.25rem' }}
      >
        <option value="">Choose…</option>
        <optgroup label="Fine to sign electronically">
          {allowed.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </optgroup>
        <optgroup label="Allowed, with a caveat">
          {warned.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </optgroup>
        <optgroup label="Cannot be signed electronically">
          {blocked.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </optgroup>
      </select>

      {!value && touched && <p className="muted" style={{ marginTop: '.5rem' }}>Required.</p>}

      {cat?.eligibility === 'block' && (
        <div className="alert" role="alert" style={{ marginTop: '.75rem' }}>
          <strong>This cannot be signed electronically.</strong>
          <p style={{ margin: '.5rem 0' }}>{cat.reason}</p>
          <p style={{ margin: 0, fontSize: '.8rem', opacity: 0.85 }}>
            <strong>Authority:</strong> {cat.authority}
          </p>
        </div>
      )}

      {cat?.eligibility === 'warn' && (
        <div className="notice" style={{ marginTop: '.75rem' }}>
          <strong>Read this before continuing.</strong>
          <p style={{ margin: '.5rem 0' }}>{cat.reason}</p>
          <p style={{ margin: '0 0 .75rem', fontSize: '.8rem' }}>
            <strong>Authority:</strong> {cat.authority}
          </p>
          <label style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', cursor: 'pointer' }}>
            <input
              type="checkbox" checked={ackd}
              onChange={(e) =>
                onAcknowledge(
                  e.target.checked
                    ? [...acknowledged.filter((x) => x !== cat.id), cat.id]
                    : acknowledged.filter((x) => x !== cat.id)
                )
              }
              style={{ marginTop: '.25rem', width: 18, height: 18 }}
            />
            <span>I understand and want to continue.</span>
          </label>
        </div>
      )}
    </div>
  );
}
